import { useCallback, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { userService } from "@/lib/api.js";
import { useAuth } from "@/context/auth.js";
import Sidebar from "./Sidebar.jsx";
import Topbar from "./Topbar.jsx";
import { ChromeSlotsContext } from "./chrome.js";

/*
 * Replaces frontend/app/components/layout/DashboardLayout.js.
 * Ported from html-markup/assets/js/shell.js.
 *
 * Pages do not render the shell — they sit in <Outlet/> and describe their
 * breadcrumb and topbar actions by rendering <PageChrome>. That is why each of
 * the markup's 15 pages shed ~250 lines of duplicated sidebar/topbar.
 *
 * Sidebar state:
 *   collapsed   desktop rail, remembered in localStorage as the Next.js app did
 *   drawerOpen  mobile off-canvas, always starts closed
 * One button drives whichever applies at the current width.
 */

const COLLAPSE_KEY = "dashboard_collapsed";

const readCollapsed = () => {
    try {
        return JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? "false") === true;
    } catch {
        return false;
    }
};

export default function DashboardLayout() {
    /*
     * The sidebar's Users badge.
     *
     * Read here rather than passed down from a page: the layout outlives every
     * page, so a prop would only ever be set on whichever page happened to know
     * the count — which is why the badge never appeared at all before.
     *
     * Same query key the Users page uses, so it is one request between them and
     * the badge updates the moment a user is added or removed there.
     */
    const { data: userCount } = useQuery({
        queryKey: ["users"],
        queryFn: () => userService.getAllUsers().then((r) => r.data?.data?.users ?? []),
        select: (users) => users.length,
        staleTime: 60_000,
    });

    // From context, not props — RequireAuth has already guaranteed a session.
    const { user } = useAuth();

    const [collapsed, setCollapsed] = useState(readCollapsed);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [slots, setSlots] = useState({ crumbs: null, actions: null });

    useEffect(() => {
        try {
            localStorage.setItem(COLLAPSE_KEY, JSON.stringify(collapsed));
        } catch {
            /* private mode — collapse still works for this session */
        }
    }, [collapsed]);

    const toggleSidebar = useCallback(() => {
        if (window.matchMedia("(min-width: 1024px)").matches) {
            setCollapsed((c) => !c);
        } else {
            setDrawerOpen((o) => !o);
        }
    }, []);

    const closeDrawer = useCallback(() => setDrawerOpen(false), []);

    // Stable so Topbar's publish effect runs once, not on every render.
    const handleSlots = useCallback((next) => {
        setSlots((prev) =>
            prev.crumbs === next.crumbs && prev.actions === next.actions ? prev : next,
        );
    }, []);

    return (
        <div className="flex min-h-full">
            <Sidebar
                collapsed={collapsed}
                drawerOpen={drawerOpen}
                onCloseDrawer={closeDrawer}
                role={user?.role}
                userCount={userCount}
            />

            <div className="flex min-w-0 flex-1 flex-col">
                <Topbar onToggleSidebar={toggleSidebar} onSlots={handleSlots} user={user} />

                <ChromeSlotsContext.Provider value={slots}>
                    <Outlet />
                </ChromeSlotsContext.Provider>
            </div>
        </div>
    );
}
